import React, {
  memo, useEffect, useMemo, useState
} from 'react';
import {
  Button, Flex, Form, PasswordInput, Text
} from 'onefaceui-react-components';
import { push } from 'connected-react-router';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { actionCreators } from 'react/api/actions/i18n';
import { actionCreators as recoverPasswordActions } from 'react/api/actions/recoverPassword';

import Loading from 'react/components/Loading';
import PasswordValidator from 'react/components/PasswordValidator';

import descartesBrand from 'static/images/brand.svg';
import defaultFetch from 'react/api/sagas/fetch/defaultFetch';

function RecoverPassword({ location }) {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const i18n = useSelector((state) => state.i18n.get('resources'));
  const {
    newPassword, confirmPassword, hintsValidated
  } = useSelector((state) => ({
    newPassword: state.recoverPassword.getIn(['password', 'newPass']),
    confirmPassword: state.recoverPassword.getIn(['password', 'confirmPass']),
    hintsValidated: state.recoverPassword.getIn(['password', 'hintsValidated'])
  }));

  async function handleSubmit(formValues) {
    const query = new URLSearchParams(location.search);
    const token = query.get('token');
    const login = query.get('login');
    const key = query.get('key');

    const { newPass, confirmPass } = formValues;

    const url = '/User/CompleteForgotPassword';
    const payload = {
      login,
      token,
      key,
      newPassword: newPass,
      confirmNewPassword: confirmPass
    };

    setIsLoading(true);

    const response = await defaultFetch(url, payload, 'POST', false);

    if (response.error) {
      const { errorMessage } = response.error;
      const { message } = errorMessage;

      setIsLoading(false);

      $.toast({
        message: i18n.get(message) || errorMessage,
        displayTime: 3000,
        inTime: 100,
        outTime: 400,
        vPosition: 'center',
        hPosition: 'center',
        className: 'toastError login'
      });

      return;
    }

    $.toast({
      message: i18n.get('user.changePassword.successMessage'),
      displayTime: 3000,
      inTime: 100,
      outTime: 400,
      vPosition: 'center',
      hPosition: 'center',
      className: 'toastSuccess login',
      maxWidth: 700
    });

    setTimeout(() => {
      dispatch(push('/login'));
    }, 3500);
  }

  function allHintsValidated(value) {
    dispatch(recoverPasswordActions.onChange({ value, id: 'hintsValidated' }));
  }

  function handleOnChange({ target: { value, name } }) {
    dispatch(recoverPasswordActions.onChange({ value, id: name }));
  }

  const {
    btnText,
    descriptionText,
    newPasswordLabel,
    confirmPasswordLabel
  } = useMemo(() => ({
    descriptionText: i18n.get(
      'users.changePasswordHeader.label',
      'Reset your password'
    ),
    newPasswordLabel: i18n.get('user.changePassword.newPassword', 'New password'),
    confirmPasswordLabel: i18n.get(
      'user.changePassword.confirmNewPassword',
      'Confirm new password'
    ),
    btnText: i18n.get('permission.user.changepassword', 'Change password')
  }), [i18n]);

  const login = useMemo(() => {
    const urlSearch = new URLSearchParams(location.search);

    return urlSearch.get('login');
  }, [location]);

  const arePassEquals = (newPassword && confirmPassword && newPassword === confirmPassword);
  const buttonEnabled = (arePassEquals && hintsValidated);

  useEffect(() => {
    function getResources() {
      dispatch(
        actionCreators.setLocale({ language: navigator.language.substring(0, 2) })
      );

      dispatch(
        actionCreators.fetchResourcesByKey([
          'users.changePasswordHeader.label',
          'user.changePassword.newPassword',
          'user.changePassword.confirmNewPassword',
          'permission.user.changepassword',
          'ForgotPasswordRequest.expired',
          'user.changePassword.successMessage'
        ])
      );
    }

    getResources();
  }, []);

  return (
    <Flex data-testid="reset-password-container" id="ResetPassword" alignItems="center" justifyContent="center">
      <Flex className="container" flexDirection="column" alignItems="center">
        <img
          className="brand"
          alt="brand"
          src={descartesBrand}
          role="presentation"
          data-testid="brand"
        />
        <Form onSubmit={handleSubmit}>
          <Flex gap={16} flexDirection="column">
            <Flex flexDirection="column">
              <Text size="sm" dataTestId="description">
                {descriptionText}
              </Text>
              <Text size="xs" title={App.Version.displayInfo}>
                {App.Version.GMLSemanticVersion}
              </Text>
            </Flex>
            <Flex flexDirection="column" gap={8}>
              <PasswordInput
                name="newPass"
                label={newPasswordLabel}
                placeholder={newPasswordLabel}
                dataTestId="input-newPassword"
                onChange={handleOnChange}
              />

              <PasswordValidator
                user={login}
                maxPerCol={3}
                newPass={newPassword}
                allHintsValidated={allHintsValidated}
              />

              <PasswordInput
                name="confirmPass"
                label={confirmPasswordLabel}
                placeholder={confirmPasswordLabel}
                dataTestId="input-confirmNewPassword"
                onChange={handleOnChange}
              />
            </Flex>
            <Button
              disabled={!buttonEnabled}
              type="submit"
              dataTestId="btn-submit"
            >
              {isLoading ? <Loading /> : btnText}
            </Button>
          </Flex>
        </Form>
      </Flex>
    </Flex>
  );
}

RecoverPassword.propTypes = {
  location: PropTypes.object.isRequired
};

export default memo(RecoverPassword);
